import React from 'react';

interface SimpleMarkdownRendererProps {
  content: string;
  isUserMessage?: boolean;
}

const SimpleMarkdownRenderer: React.FC<SimpleMarkdownRendererProps> = ({ content, isUserMessage = false }) => {
  // Clean all XML citation tags from content
  const cleanContent = (text: string): string => {
    let cleaned = text;

    // Step 1: Handle complete answer_part blocks - extract text and remove sources
    cleaned = cleaned.replace(
      /<answer_part>\s*<text>([\s\S]*?)<\/text>\s*<sources>[\s\S]*?<\/sources>\s*<\/answer_part>/gi,
      '$1',
    );

    // Step 2: Handle answer_part blocks without sources
    cleaned = cleaned.replace(/<answer_part>\s*<text>([\s\S]*?)<\/text>\s*<\/answer_part>/gi, '$1');

    // Step 3: Extract any remaining text content from standalone <text> tags
    cleaned = cleaned.replace(/<text>([\s\S]*?)<\/text>/gi, '$1');

    // Step 4: Remove all XML-style tags (more comprehensive)
    cleaned = cleaned.replace(/<\/?answer_part[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?sources[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?source[^>]*>/gi, '');
    cleaned = cleaned.replace(/<\/?text[^>]*>/gi, '');

    // Step 5: Remove any remaining XML tags with numbers (like <source>1</source>, <source>2</source>)
    cleaned = cleaned.replace(/<source[^>]*>[\s\S]*?<\/source>/gi, '');
    cleaned = cleaned.replace(/<\/text>\s*<sources>/gi, '');
    cleaned = cleaned.replace(/<\/sources>\s*<\/answer_part>/gi, '');

    // Step 6: Clean up any orphaned XML fragments
    cleaned = cleaned.replace(/<[^>]+>/g, ''); // Remove any remaining XML tags

    // Step 7: Clean up whitespace and line breaks
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove triple+ line breaks
    cleaned = cleaned.replace(/^\s+|\s+$/g, ''); // Trim start/end whitespace

    return cleaned;
  };

  // Process the content to handle basic markdown and formatting
  const processMarkdown = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    let inOrderedList = false;
    let inUnorderedList = false;
    let listItems: React.ReactNode[] = [];
    const result: React.ReactNode[] = [];

    const processInlineFormatting = (text: string): React.ReactNode => {
      // Handle bold text **text**
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
    };

    const closeCurrentList = () => {
      if (inOrderedList && listItems.length > 0) {
        result.push(
          <ol key={`list-${result.length}`} className="list-decimal pl-5 my-2 space-y-1">
            {listItems}
          </ol>,
        );
      } else if (inUnorderedList && listItems.length > 0) {
        result.push(
          <ul key={`list-${result.length}`} className="list-disc pl-5 my-2 space-y-1">
            {listItems}
          </ul>,
        );
      }
      listItems = [];
      inOrderedList = false;
      inUnorderedList = false;
    };

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      // Handle ordered lists (1. 2. 3. etc)
      const orderedListMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
      if (orderedListMatch) {
        if (inUnorderedList) closeCurrentList();
        if (!inOrderedList) inOrderedList = true;

        const [, number, content] = orderedListMatch;
        listItems.push(
          <li key={`${index}-${number}`} value={parseInt(number)}>
            {processInlineFormatting(content)}
          </li>,
        );
        return;
      }

      // Handle unordered lists (- or * at start)
      const unorderedListMatch = trimmedLine.match(/^[-*]\s+(.+)$/);
      if (unorderedListMatch) {
        if (inOrderedList) closeCurrentList();
        if (!inUnorderedList) inUnorderedList = true;

        const [, content] = unorderedListMatch;
        listItems.push(<li key={`${index}-bullet`}>{processInlineFormatting(content)}</li>);
        return;
      }

      // If we were in a list and hit a non-list line, close the list
      if ((inOrderedList || inUnorderedList) && !orderedListMatch && !unorderedListMatch) {
        closeCurrentList();
      }

      // Handle headers (## Header)
      const headerMatch = trimmedLine.match(/^(#{1,3})\s+(.+)$/);
      if (headerMatch) {
        const [, hashes, content] = headerMatch;
        const level = hashes.length;
        const HeaderTag = `h${Math.min(level + 2, 6)}` as keyof JSX.IntrinsicElements; // h3, h4, h5, h6
        const headerClass =
          level === 1
            ? 'text-lg font-semibold mt-4 mb-2'
            : level === 2
              ? 'text-base font-semibold mt-3 mb-2'
              : 'text-sm font-semibold mt-2 mb-1';

        result.push(
          <HeaderTag key={index} className={headerClass}>
            {processInlineFormatting(content)}
          </HeaderTag>,
        );
        return;
      }

      // Handle regular paragraphs
      if (trimmedLine) {
        result.push(
          <p key={index} className={isUserMessage ? 'my-0.5 leading-relaxed' : 'my-2 leading-relaxed'}>
            {processInlineFormatting(trimmedLine)}
          </p>,
        );
      } else if (!inOrderedList && !inUnorderedList) {
        // Add spacing between paragraphs, but not within lists
        result.push(<div key={index} className={isUserMessage ? 'h-1' : 'h-2'} />);
      }
    });

    // Close any remaining list
    closeCurrentList();

    return result;
  };

  return <div className="markdown-content">{processMarkdown(cleanContent(content))}</div>;
};

export default SimpleMarkdownRenderer;
